/**
 * @namespace transform/number
 * @see module:@kids/lib/transform/number
 */

/**
 * @module @kids/lib/transform/number
 * @see transform/number
 * @desc Stellt alle puren Funktionen zur Number-Umwandlung zur Verfügung.
 * @example
 * import {roundFractions} from '@kids/lib/transform/number';
 *
 * roundFractions(3.14159265359, 1)); // -> 3.1
 */

const {Math} = globalThis;

/** Rundet eine Zahl auf eine definierte Anahl von Nachkommastellen.
  * @alias transform/number.roundFractions
  * @param {number} number
  * @param {number} [decimalPlace=2] - Anzahl der Nachkommastellen
  * @return {number} die gerundete Zahl
  */
export function roundFractions(number, decimalPlace = 2)
{
    if (number === 0) { return 0; }
    decimalPlace = 10 ** decimalPlace;
    return Math.round(number * decimalPlace) / decimalPlace;
}

/** Rundet eine Zahl zum nächsten Vielfachen der übergebenen Zahl.
  * @alias transform/number.getClosestMultipleOf
  * @param {number} number
  * @param {number} multiple - Ein Vielfaches dieser Zahl soll zurückgegeben werden
  * @returns {number}
  */
export function getClosestMultipleOf(number, multiple)
{
    const rest = number % multiple;
    if (rest <= (multiple / 2))
    {
        return number - rest;
    }
    return number + multiple - rest;
}

/** Wandelt eine Anzahl von Sekunden in Tage, Stunden, Minuten und Sekunden.
  * @alias transform/number.toTimeUnits
  * @param {number} number
  * @return {Object.<number>} ``days`` Tage, ``hours`` Stunden, ``minutes`` Minuten, ``seconds`` Sekunden
  */
export function toTimeUnits(number, roundSeconds = true)
{
    const units = {};
    let restSeconds = number;

    units.days = Math.floor(restSeconds / 86400);
    restSeconds %= 86400;

    units.hours = Math.floor(restSeconds / 3600);
    restSeconds %= 3600;

    units.minutes = Math.floor(restSeconds / 60);
    units.seconds = restSeconds % 60;

    if (roundSeconds)
    {
        units.seconds = Math.floor(units.seconds);
    }

    return units;
}

export function toSexagesimal(number, pad = true, seperator = ':')
{
    console.warn('"toSexagesimal" is depracated. Please use "toTimeString"!');
    const units = toTimeUnits(number);
    let string = '';

    if (units.hours > 0)
    {
        string += units.hours;
        if (units.hours < 10 && pad)
        {
            string = `0${string}`;
        }
        string += seperator;
    }

    string += (units.minutes < 10 && (pad || string) ? '0' : '') + units.minutes + seperator;
    string += (units.seconds < 10 ? '0' : '') + units.seconds;
    return string;
}

/** Wandelt eine Anzahl von Sekunden in einen Zeit-String um.
  * @alias transform/number.toTimeString
  * @param {number} totalSeconds
  * @param {string} [format="mm:ss"] Ein Format, welches die Platzhalter H, HH, m, mm, s und ss nutzen kann.
  * @param {bool} roundSeconds Entscheidet, ob übrige Millisekunden entfernt werden
  * @return {string}
  */
export function toTimeString(totalSeconds, format = 'mm:ss', roundSeconds = true)
{
    const units = toTimeUnits(totalSeconds, roundSeconds);
    const hours = `${units.hours}`;
    const minutes = `${units.minutes}`;
    const seconds = `${units.seconds}`;

    return format
        .replace('HH', hours.padStart(2, '0')).replace('H', hours)
        .replace('mm', minutes.padStart(2, '0')).replace('m', minutes)
        .replace('ss', seconds.padStart(2, '0'))
        .replace('s', seconds);
}

/** Wandelt eine Zahl vom Input-Range zum Output-Rang um.
  * @alias transform/number.mapRange
  * @param {number} value
  * @param {number} inMin
  * @param {number} inMax
  * @param {number} outMin
  * @param {number} outMax
  * @return {number}
  */
export function mapRange(value, inMin, inMax, outMin, outMax)
{
    return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}
